import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { transformed } from './viewModel';

const mastersOf = form => [{ id: 'front', fid: form.image_file_id }, ...(form.views || []).filter(v => v.image_file_id).map(v => ({ id: v.id, fid: v.image_file_id }))].filter(v => v.fid);

// Which colour photos the recolour engine has to (re)make: missing ones, or own engine photos whose master or hex changed.
const pending = form => {
  const jobs = {};
  for (const v of mastersOf(form)) (form.colors || []).slice(1).forEach(c => {
    const want = `${v.fid}|${c.hex.toLowerCase()}`, own = c.image_file_ids?.[v.id], made = c.recolored?.[v.id];
    if (!own || (made && made !== want)) (jobs[v.id] ||= { fid: v.fid, hexes: new Set() }).hexes.add(c.hex.toLowerCase());
  });
  return jobs;
};

const applyFiles = (old, viewId, master, files) => ({ ...old, colors: (old.colors || []).map((c, i) => {
  const h = c.hex.toLowerCase(), fid = files[h];
  if (!i || !fid || mastersOf(old).find(v => v.id === viewId)?.fid !== master) return c;
  if (c.image_file_ids?.[viewId] && !c.recolored?.[viewId]) return c;
  return { ...c, image_file_ids: { ...(c.image_file_ids || {}), [viewId]: fid }, recolored: { ...(c.recolored || {}), [viewId]: `${master}|${h}` } };
}) });

// Textile master photos: back is aligned to the front once, every other colour is recoloured pixel-exactly from the master.
export const useTextileMaster = (form, setForm) => {
  const busy = useRef(false), aligned = useRef(''), failed = useRef(''), [tick, setTick] = useState(0);
  const back = (form.views || []).find(v => v.id === 'back')?.image_file_id;
  const key = form.kind === 'textile' ? JSON.stringify([form.image_file_id, back, mastersOf(form), (form.colors || []).map(c => [c.hex, c.image_file_ids, c.recolored])]) : '';
  useEffect(() => {
    if (!key || busy.current) return;
    const timer = setTimeout(async () => {
      busy.current = true;
      let did = false;
      try {
        if (form.image_file_id && back && aligned.current !== `${form.image_file_id}|${back}`) {
          did = true;
          const r = await adminApi('/product-views/align', { method: 'POST', body: { reference_file_id: form.image_file_id, file_ids: [back], transparent: true } });
          aligned.current = `${form.image_file_id}|${r.files[back] || back}`;
          if (r.files[back]) { setForm(old => ({ ...old, ...transformed(old, 'back', r.transforms[back], r.files) })); toast.success('Rückseite an die Vorderseite angeglichen.'); return; }
        }
        const jobs = Object.entries(pending(form));
        if (!jobs.length || failed.current === key) return;
        did = true;
        const id = toast.loading('Farbfotos werden aus dem Master eingefärbt …');
        try {
          for (const [viewId, job] of jobs) {
            const { files } = await adminApi('/product-views/recolor', { method: 'POST', body: { source_file_id: job.fid, hexes: [...job.hexes] } });
            setForm(old => applyFiles(old, viewId, job.fid, files));
          }
          toast.success('Farbfotos aus dem Master erstellt.', { id });
        } catch (e) { failed.current = key; toast.error(e.message, { id }); }
      } catch (e) { toast.error(e.message); } finally { busy.current = false; if (did) setTick(t => t + 1); }
    }, 700);
    return () => clearTimeout(timer);
  }, [key, tick]); // eslint-disable-line react-hooks/exhaustive-deps
};
